import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSubmissionQueries from '@dashboard/hooks/queries/useSubmissionQueries';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import Dash from '@dashboard/components/Dash';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import StatusBadge from '@dashboard/components/StatusBadge';
import { formatDateTime } from '@dashboard/lib/format-date';
import getSubmissionStatusLabel from '@dashboard/services/status/getSubmissionStatusLabel';
import PageTitle from '@dashboard/components/PageTitle';

const FILTER_OPTIONS = {
  ALL: {
    label: 'All',
    value: 'all',
  },
  SUCCEEDED: {
    label: 'Succeeded',
    value: 'succeeded',
  },
  FAILED: {
    label: 'Failed',
    value: 'failed',
  },
  CANCELED: {
    label: 'Canceled',
    value: 'canceled',
  },
};

export function getSubmissionColumns({ options = {} } = {}) {
  const columns = [];

  columns.push({
    accessorKey: 'customerEmail',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
    cell: ({ row }) => {
      const submission = row.original;
      return (
        <div className="max-w-xs">
          <div className="font-medium">
            {submission.customerEmail || submission.billing?.email || <Dash />}
          </div>
          <div className="text-sm text-gray-500 dark:text-muted-foreground">
            {submission.customerName || submission.billing?.name || ''}
          </div>
        </div>
      );
    },
    enableSorting: true,
  });

  if (!options.hidePageName) {
    columns.push({
      id: 'page',
      header: 'Form',
      cell: ({ row }) => (row.original.page && <PageTitle page={row.original.page} />) || <Dash />,
      enableSorting: false,
    });
  }

  columns.push({
    accessorKey: 'createdAt',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    enableSorting: true,
  });

  if (!options.hideStatus) {
    columns.push({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const submission = row.original;
        if (!submission.livemode) {
          return <StatusBadge status="test" label="Test" />;
        }
        const status = submission.orderStatus === 'canceled' ? 'canceled' : submission.status;
        return <StatusBadge status={status} label={getSubmissionStatusLabel(status)} />;
      },
      enableSorting: false,
    });
  }

  return columns;
}

export default function Submissions() {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, FILTER_OPTIONS);
  const page = query.page || undefined;

  const filter = {
    ...(FILTER_OPTIONS[status].value !== 'all' && {
      status: FILTER_OPTIONS[status].value,
    }),
    livemode: true,
    page: page || undefined,
  };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: FILTER_OPTIONS[key].value === 'all' ? undefined : FILTER_OPTIONS[key].value,
    });

  const handlePageChange = value => setParams({ page: value || undefined });

  const leftFilterControls = (
    <div className="w-full md:w-64">
      <PageFilterInput
        name="page"
        pageTypes={['form']}
        onChange={handlePageChange}
        initialValue={query.page}
        placeholder="Filter by form"
      />
    </div>
  );

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2 max-md:w-full [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 lg:[&>*:first-child]:flex-none max-sm:[&>*:first-child]:basis-full">
      <SearchInput
        onChange={handleSearchChange}
        name="search_submissions"
        placeholder="Search by email"
        initialValue={search}
      />
      <FilterInput options={FILTER_OPTIONS} onChange={handleStatusChange} value={status} />
      <ExportView
        collection="submissions"
        label="submissions"
        filters={{
          ...filter,
          sort: JSON.stringify(sort),
          search,
        }}
      />
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <PageHeader title="Submissions" />

      <SubmissionsList
        sort={sort}
        search={search}
        filter={filter}
        filterControls={filterControls}
        leftFilterControls={leftFilterControls}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
}

export function SubmissionsList({
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
  emptyStateSubtitle,
  filterControls,
  leftFilterControls,
  sorting: sortingProp,
  onSortingChange,
}) {
  const { sellerId } = useAuth();
  const { getInfiniteSubmissions } = useSubmissionQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['submission', sellerId, sort, search, filter, refreshKey],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getInfiniteSubmissions(sellerId, {
          ...filter,
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!infinite) return undefined;
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // react-table mounts each `cell` function as a component: a new columns array remounts every cell.
  const { hidePageName, hideStatus } = options;
  const columns = useMemo(
    () => getSubmissionColumns({ options: { hidePageName, hideStatus } }),
    [hidePageName, hideStatus],
  );

  const isEmpty = !isLoading && flatData.length === 0;

  let emptyState;
  if (isEmpty && search) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No submissions found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && emptyStateSubtitle) {
    emptyState = (
      <EmptyState>
        <EmptyState.Subtitle>{emptyStateSubtitle}</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && filter.livemode !== false) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No submissions yet</EmptyState.Title>
        <EmptyState.Subtitle>Your submissions will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && filter.livemode === false) {
    emptyState = (
      <EmptyState>
        <EmptyState.Subtitle>
          {emptyStateSubtitle || 'Test submissions will be shown here.'}
        </EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isError) {
    return (
      <div className="m-4">Unable to show submissions. Please refresh or contact support.</div>
    );
  }

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode={infinite ? 'infinite' : undefined}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sortingProp}
      onSortingChange={onSortingChange}
      toolbar={
        filterControls || leftFilterControls
          ? {
              leftActions: leftFilterControls,
              customActions: filterControls,
            }
          : undefined
      }
      getRowHref={submission => `/submissions/${submission._id}`}
      emptyState={emptyState}
      emptyMessage="No submissions found"
    />
  );
}
